import React, { useEffect, useMemo, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlayer } from '../../contexts/PlayerContext';

// Helper to ensure we have a portal root
function usePortalRoot() {
  const root = useMemo(() => {
    let el = document.getElementById('global-video-overlay-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'global-video-overlay-root';
      document.body.appendChild(el);
    }
    return el;
  }, []);
  return root;
}

export default function GlobalVideoOverlay() {
  const { isActive, videoId, mode, anchorEl, minimizeSource, close, restore, minimize, load, consumePlayIntent } = usePlayer();
  const portalRoot = usePortalRoot();
  const location = useLocation();
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const ytRef = useRef(null);
  const rafRef = useRef(null);
  const dragRef = useRef(false);
  const wantInlineRestoreRef = useRef(false);
  const lastInlineRectRef = useRef(null);
  const lastLoadedVideoRef = useRef(null);
  const resumeAfterRemountRef = useRef(null);

  const [inlineRect, setInlineRect] = useState(null);
  const [dock, setDock] = useState('bottom-left'); // 'top-left' | 'top-right' | 'middle-left' | 'middle-right' | 'bottom-left' | 'bottom-right'
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState(null); // { top, left }
  const dragStartRef = useRef(null); // { x, y, top, left }
  const dragMovedRef = useRef(false);
  const DRAG_THRESHOLD_PX = 6;

  // Route-aware flags
  const onShorts = location.pathname.startsWith('/shorts');

  // Position overlay to follow anchor in inline mode
  useEffect(() => {
    const updatePosition = () => {
      if (mode === 'inline' && anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        // Sometimes width/height may read 0 for a tick; schedule a re-measure
        if (!rect.width || !rect.height) {
          if (rafRef.current) cancelAnimationFrame(rafRef.current);
          rafRef.current = requestAnimationFrame(() => {
            const r2 = anchorEl.getBoundingClientRect();
            setInlineRect({ ...r2 });
            if (r2.width && r2.height) lastInlineRectRef.current = { ...r2 };
          });
        } else {
          setInlineRect({ ...rect });
          lastInlineRectRef.current = { ...rect };
        }
      }
    };

    updatePosition();

    const onScroll = () => {
      if (mode === 'inline' && anchorEl) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(updatePosition);
      }
    };

    // Observe anchor size changes for robust measurement
    let ro;
    try {
      if (window.ResizeObserver && anchorEl) {
        ro = new ResizeObserver(() => {
          if (mode === 'inline' && anchorEl) {
            const r = anchorEl.getBoundingClientRect();
            setInlineRect({ ...r });
          }
        });
        ro.observe(anchorEl);
      }
    } catch {}

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      try { ro && ro.disconnect(); } catch {}
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [mode, anchorEl]);

  // Pre-measure inline rect before paint to avoid an initial flash
  useLayoutEffect(() => {
    if (mode === 'inline' && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      setInlineRect({ ...rect });
      if (rect.width && rect.height) lastInlineRectRef.current = { ...rect };
    }
  }, [mode, anchorEl]);

  // Initialize YT player; validate existing instance and destroy on deactivation/unmount
  useEffect(() => {
    // If overlay is not active, ensure player is torn down to avoid stale refs
    if (!isActive) {
      if (ytRef.current) {
        try { ytRef.current.destroy?.(); } catch {}
        ytRef.current = null;
      }
      return;
    }

    const ensureYT = () => {
      if (window.YT && window.YT.Player) return Promise.resolve();
      return new Promise((resolve) => {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          prev && prev();
          resolve();
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(tag);
        }
      });
    };

    let didCleanup = false;

    ensureYT().then(() => {
      if (didCleanup) return;

      // Validate existing player; clear orphaned instances (e.g., after close)
      if (ytRef.current) {
        try {
          ytRef.current.getPlayerState?.();
          return; // valid, reuse
        } catch {
          try { ytRef.current.destroy?.(); } catch {}
          ytRef.current = null;
        }
      }

      const dispatch = (type) => {
        try {
          window.dispatchEvent(new CustomEvent('penta-video-player', { detail: { type, videoId: () => ytRef.current?.getVideoData?.().video_id || videoId } }));
        } catch {}
      };

      ytRef.current = new window.YT.Player(playerRef.current, {
        host: 'https://www.youtube.com',
        videoId: videoId || undefined,
        playerVars: {
          autoplay: 0,
          mute: 0,
          controls: 0, // hide native controls; we render our own
          disablekb: 0,
          fs: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          origin: window.location.origin,
          enablejsapi: 1,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try { setDuration(e.target.getDuration?.() || 0); } catch {}
            try {
              const shouldPlay = typeof consumePlayIntent === 'function' ? consumePlayIntent() : false;
              if (shouldPlay) e.target.playVideo();
            } catch {}
            try {
              const iframe = e.target.getIframe();
              if (iframe) {
                iframe.setAttribute('allowfullscreen', 'true');
                iframe.setAttribute('webkitallowfullscreen', 'true');
                iframe.setAttribute('mozallowfullscreen', 'true');
                iframe.setAttribute('msallowfullscreen', 'true');
                iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
                iframe.setAttribute('playsinline', 'true');
                iframe.setAttribute('webkit-playsinline', 'true');
                iframe.style.position = 'absolute';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.display = 'block';
                iframe.style.border = '0';
              }
            } catch {}
          },
          onStateChange: (e) => {
            const YT = window.YT;
            if (!YT) return;
            switch (e.data) {
              case YT.PlayerState.PLAYING:
                dispatch('play');
                setIsPlaying(true);
                break;
              case YT.PlayerState.PAUSED:
                dispatch('pause');
                setIsPlaying(false);
                break;
              case YT.PlayerState.ENDED:
                dispatch('ended');
                setIsPlaying(false);
                break;
              default:
                break;
            }
          },
          onError: (e) => {
            try { console.warn('[GlobalVideoOverlay] YouTube player error:', e?.data); } catch {}
          },
        },
      });
    });

    return () => {
      didCleanup = true;
      // Always destroy on unmount to avoid orphan refs
      if (ytRef.current) {
        try { ytRef.current.destroy?.(); } catch {}
        ytRef.current = null;
      }
    };
  }, [isActive]);

  // Load new video only when videoId actually changes; preserve time/play state across mode switches.
  useEffect(() => {
    if (!ytRef.current || !videoId) return;
    const player = ytRef.current;
    const YT = window.YT;

    const tryPlay = () => {
      try { player.playVideo(); } catch {}
      setTimeout(() => {
        try {
          const st = player.getPlayerState?.();
          if (YT && st !== YT.PlayerState.PLAYING) player.playVideo();
        } catch {}
      }, 120);
    };

    // If the same video is already loaded, don't reload; only honor play intent.
    let currentId;
    try { currentId = player.getVideoData?.().video_id; } catch {}
    if (currentId === videoId || lastLoadedVideoRef.current === videoId) {
      const shouldPlay = typeof consumePlayIntent === 'function' ? consumePlayIntent() : false;
      if (shouldPlay) tryPlay();
      return;
    }

    const shouldPlay = typeof consumePlayIntent === 'function' ? consumePlayIntent() : false;
    if (shouldPlay) {
      try {
        player.loadVideoById(videoId);
        lastLoadedVideoRef.current = videoId;
        try { setDuration(player.getDuration?.() || 0); } catch {}
        tryPlay();
      } catch (e) {
        try {
          player.cueVideoById(videoId);
          lastLoadedVideoRef.current = videoId;
          setTimeout(tryPlay, 120);
        } catch {}
      }
    } else {
      try {
        player.cueVideoById(videoId);
        lastLoadedVideoRef.current = videoId;
        try { setDuration(player.getDuration?.() || 0); } catch {}
      } catch {}
    }
  }, [videoId]);

  // Pause and hide interaction on Shorts routes (keep in DOM for iOS stability)
  useEffect(() => {
    if (!ytRef.current) return;
    if (onShorts) {
      try { ytRef.current.pauseVideo(); } catch {}
    }
  }, [onShorts]);

  // Keep YT player sized to container whenever layout changes
  useEffect(() => {
    const resizeToContainer = () => {
      if (!ytRef.current || !containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      if (r.width && r.height) {
        try { ytRef.current.setSize(r.width, r.height); } catch {}
      }
    };
    resizeToContainer();
    window.addEventListener('resize', resizeToContainer);
    return () => window.removeEventListener('resize', resizeToContainer);
  }, [inlineRect, mode]);

  // ResizeObserver to react to container size changes (e.g., after fullscreen transitions)
  useEffect(() => {
    if (!containerRef.current || !window.ResizeObserver) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      try {
        const r = el.getBoundingClientRect();
        if (ytRef.current && r.width && r.height) {
          ytRef.current.setSize(r.width, r.height);
          try {
            const iframe = ytRef.current.getIframe?.();
            if (iframe) {
              iframe.style.position = 'absolute';
              iframe.style.top = '0';
              iframe.style.left = '0';
              iframe.style.width = '100%';
              iframe.style.height = '100%';
            }
          } catch {}
        }
      } catch {}
    });
    ro.observe(el);
    return () => { try { ro.disconnect(); } catch {} };
  }, [isActive, mode, isFullscreen]);

  // Track time via rAF for smooth progress updates
  useEffect(() => {
    let raf;
    const tick = () => {
      try {
        if (ytRef.current) {
          const t = ytRef.current.getCurrentTime?.() || 0;
          const d = ytRef.current.getDuration?.() || 0;
          setCurrentTime(t);
          if (d) setDuration(d);
        }
      } catch {}
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [isActive, videoId]);

  const formatTime = (sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const Icons = {
    play: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M8 5v14l11-7z" />
      </svg>
    ),
    pause: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
      </svg>
    ),
    playLarge: (
      <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M8 5v14l11-7z" />
      </svg>
    ),
    close: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z" />
      </svg>
    ),
    minimize: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M19 13H5v-2h14v2z" />
      </svg>
    ),
    maximize: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M4 4h16v16H4V4zm2 2v12h12V6H6z" />
      </svg>
    ),
    drag: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M10 4h4v2h-4V4zm0 14h4v2h-4v-2zM4 10h2v4H4v-4zm14 0h2v4h-2v-4z" />
      </svg>
    ),
    fullscreen: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
      </svg>
    ),
    fullscreenExit: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
      </svg>
    ),
  };

  const handleTogglePlay = useCallback(() => {
    if (!ytRef.current) return;
    const YT = window.YT;
    try {
      const st = ytRef.current.getPlayerState?.();
      if (st === YT.PlayerState.PLAYING) ytRef.current.pauseVideo();
      else ytRef.current.playVideo();
    } catch {}
  }, []);

  const handleSeek = (e) => {
    if (!containerRef.current || !ytRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = (touch ? touch.clientX : e.clientX);
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const target = ratio * duration;
    try { ytRef.current.seekTo(target, true); } catch {}
  };

  // Fullscreen logic
  useEffect(() => {
    const onFsChange = () => {
      const fsEl = document.fullscreenElement || 
                   document.webkitFullscreenElement || 
                   document.mozFullScreenElement || 
                   document.msFullscreenElement;
      
      // Check if either the container or portal root is in fullscreen
      const portalEl = document.getElementById('global-video-overlay-root');
      const isInFullscreen = fsEl === containerRef.current || fsEl === portalEl;
      
      console.log('[GlobalVideoOverlay] Fullscreen change detected, element:', fsEl, 'isFullscreen:', isInFullscreen);
      setIsFullscreen(isInFullscreen);
      
      // Ensure container styles reflect fullscreen state even when exit is via ESC/system UI
      const containerEl = containerRef.current;
      if (containerEl) {
        if (isInFullscreen) {
          containerEl.style.width = '100vw';
          containerEl.style.height = '100vh';
          containerEl.style.maxWidth = '100vw';
          containerEl.style.maxHeight = '100vh';
        } else {
          containerEl.style.width = '';
          containerEl.style.height = '';
          containerEl.style.maxWidth = '';
          containerEl.style.maxHeight = '';
        }
      }
      
      // Resize the player when entering/exiting fullscreen
      setTimeout(() => {
        try {
          if (ytRef.current && containerRef.current) {
            const r = containerRef.current.getBoundingClientRect();
            console.log('[GlobalVideoOverlay] Resizing player to:', r.width, 'x', r.height);
            ytRef.current.setSize(r.width, r.height);
            // Re-apply iframe layout to ensure it pins to all edges after transition
            try {
              const iframe = ytRef.current.getIframe?.();
              if (iframe) {
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
              }
            } catch {}
          }
        } catch (err) {
          console.error('[GlobalVideoOverlay] Error resizing player:', err);
        }
      }, 100); // Small delay to ensure fullscreen transition is complete
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    document.addEventListener('MSFullscreenChange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      document.removeEventListener('MSFullscreenChange', onFsChange);
    };
  }, []);

  const enterFullscreen = useCallback(() => {
    // Detect if we're on a mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // For mobile, try the video iframe directly first
    if (isMobile && ytRef.current) {
      try {
        const iframe = ytRef.current.getIframe();
        if (iframe) {
          // On iOS, we need to use webkitEnterFullscreen on the video element
          const videoElement = iframe.contentWindow?.document?.querySelector('video');
          if (videoElement && videoElement.webkitEnterFullscreen) {
            console.log('[GlobalVideoOverlay] Using iOS video fullscreen');
            videoElement.webkitEnterFullscreen();
            return;
          }
        }
      } catch (err) {
        console.log('[GlobalVideoOverlay] Could not access video element:', err);
      }
    }
    
    // Standard fullscreen approach
    const portalEl = document.getElementById('global-video-overlay-root');
    const containerEl = containerRef.current;
    
    // Try portal root first, then container as fallback
    const elementsToTry = [portalEl, containerEl].filter(Boolean);
    
    let fullscreenAttempted = false;
    let lastError = null;
    
    for (const el of elementsToTry) {
      if (!el) continue;
      
      console.log('[GlobalVideoOverlay] Attempting fullscreen on:', el.id || 'container');
      
      // Try different fullscreen APIs with mobile-specific order
      const requestFS = el.requestFullscreen || 
                        el.webkitRequestFullscreen || 
                        el.webkitRequestFullScreen || // Note: capital S for older Safari
                        el.mozRequestFullScreen || 
                        el.msRequestFullscreen;
      
      if (requestFS) {
        fullscreenAttempted = true;
        try {
          const promise = requestFS.call(el);
          
          // Handle the promise if it exists
          if (promise && typeof promise.then === 'function') {
            promise
              .then(() => {
                console.log('[GlobalVideoOverlay] Successfully entered fullscreen on:', el.id || 'container');
                setIsFullscreen(true);
                // Ensure the container fills the screen
                if (containerEl) {
                  containerEl.style.width = '100vw';
                  containerEl.style.height = '100vh';
                  containerEl.style.maxWidth = '100vw';
                  containerEl.style.maxHeight = '100vh';
                }
              })
              // eslint-disable-next-line no-loop-func
              .catch((err) => {
                lastError = err;
                console.error('[GlobalVideoOverlay] Fullscreen failed on', el.id || 'container', ':', err);
                // Continue to try next element
              });
            return; // Exit if promise was created
          } else {
            // For older browsers that don't return a promise
            console.log('[GlobalVideoOverlay] Fullscreen requested (no promise) on:', el.id || 'container');
            setIsFullscreen(true);
            if (containerEl) {
              containerEl.style.width = '100vw';
              containerEl.style.height = '100vh';
              containerEl.style.maxWidth = '100vw';
              containerEl.style.maxHeight = '100vh';
            }
            return; // Exit after successful attempt
          }
        } catch (err) {
          lastError = err;
          console.error('[GlobalVideoOverlay] Fullscreen error:', err);
        }
      }
    }
    
    // If all attempts failed, provide mobile-specific message
    if (!fullscreenAttempted || lastError) {
      console.warn('[GlobalVideoOverlay] Fullscreen not available. Last error:', lastError);
      
      if (isMobile) {
        // For mobile, try a different approach - maximize the player
        if (containerEl) {
          if (window.__DEBUG_LOG) {
            window.__DEBUG_LOG('enter_pseudo_fullscreen', {
              message: 'Starting pseudo-fullscreen for mobile'
            });
          }
          
          containerEl.style.position = 'fixed';
          containerEl.style.top = '0';
          containerEl.style.left = '0';
          containerEl.style.width = '100vw';
          // Use dynamic viewport height with fallback to regular vh
          containerEl.style.height = '100dvh';
          if (!CSS.supports('height', '100dvh')) {
            containerEl.style.height = '100vh';
          }
          containerEl.style.zIndex = '2147483647';
          containerEl.style.background = '#000';
          
          // Set a flag to track pseudo-fullscreen
          containerEl.dataset.pseudoFullscreen = 'true';
          setIsFullscreen(true);
          
          console.log('[GlobalVideoOverlay] Using pseudo-fullscreen for mobile');
          
          if (window.__DEBUG_LOG) {
            window.__DEBUG_LOG('pseudo_fullscreen_applied', {
              styles: containerEl.style.cssText
            });
          }
          
          // Try to lock orientation to landscape on mobile
          if (window.screen && window.screen.orientation && window.screen.orientation.lock) {
            window.screen.orientation.lock('landscape').catch(() => {
              console.log('[GlobalVideoOverlay] Could not lock orientation');
            });
          }
          
          // Hide mobile browser UI if possible
          if (window.scrollTo) {
            window.scrollTo(0, 1);
          }
          
          // Resize the player
          setTimeout(() => {
            if (ytRef.current) {
              ytRef.current.setSize(window.innerWidth, window.innerHeight);
            }
          }, 100);
        }
      } else {
        alert('Fullscreen is not supported in this browser. On desktop, try pressing F11 for browser fullscreen.');
      }
    }
  }, []);
  
  // Helper function to wait for viewport to settle after fullscreen exit
  const waitForViewportSettle = useCallback(() => {
    return new Promise((resolve) => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (!isMobile) {
        // On desktop, resolve immediately
        if (window.__DEBUG_LOG) {
          window.__DEBUG_LOG('viewport_settle_skip', { reason: 'desktop' });
        }
        resolve();
        return;
      }
      
      // For mobile devices, wait for viewport to stabilize
      if (window.__DEBUG_LOG) {
        window.__DEBUG_LOG('viewport_settle_start', {
          method: window.visualViewport ? 'visualViewport' : 'fallback'
        });
      }
      
      if (window.visualViewport) {
        // Modern approach: use VisualViewport API
        let settleTimeout;
        let lastHeight = window.visualViewport.height;
        let lastWidth = window.visualViewport.width;
        let checkCount = 0;
        
        const checkStable = () => {
          const currentHeight = window.visualViewport.height;
          const currentWidth = window.visualViewport.width;
          checkCount++;
          
          if (window.__DEBUG_LOG) {
            window.__DEBUG_LOG('viewport_check', {
              checkNum: checkCount,
              currentH: currentHeight,
              currentW: currentWidth,
              lastH: lastHeight,
              lastW: lastWidth,
              stable: Math.abs(currentHeight - lastHeight) < 1 && Math.abs(currentWidth - lastWidth) < 1
            });
          }
          
          if (Math.abs(currentHeight - lastHeight) < 1 && Math.abs(currentWidth - lastWidth) < 1) {
            // Viewport is stable
            if (window.__DEBUG_LOG) {
              window.__DEBUG_LOG('viewport_settled', {
                finalH: currentHeight,
                finalW: currentWidth,
                checks: checkCount
              });
            }
            clearTimeout(settleTimeout);
            window.visualViewport.removeEventListener('resize', onViewportChange);
            window.visualViewport.removeEventListener('scroll', onViewportChange);
            resolve();
          } else {
            lastHeight = currentHeight;
            lastWidth = currentWidth;
            clearTimeout(settleTimeout);
            settleTimeout = setTimeout(checkStable, 150);
          }
        };
        
        const onViewportChange = () => {
          clearTimeout(settleTimeout);
          settleTimeout = setTimeout(checkStable, 150);
        };
        
        window.visualViewport.addEventListener('resize', onViewportChange);
        window.visualViewport.addEventListener('scroll', onViewportChange);
        
        // Start checking
        settleTimeout = setTimeout(checkStable, 150);
        
        // Fallback timeout in case events don't fire
        setTimeout(() => {
          clearTimeout(settleTimeout);
          window.visualViewport.removeEventListener('resize', onViewportChange);
          window.visualViewport.removeEventListener('scroll', onViewportChange);
          resolve();
        }, 500);
      } else {
        // Fallback for older browsers: use double RAF + timeout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 250);
          });
        });
      }
    });
  }, []);
  
  const exitFullscreen = useCallback(async () => {
    const containerEl = containerRef.current;
    
    // Check if we're in pseudo-fullscreen mode (mobile fallback)
    if (containerEl && containerEl.dataset.pseudoFullscreen === 'true') {
      console.log('[GlobalVideoOverlay] Exiting pseudo-fullscreen');
      
      if (window.__DEBUG_LOG) {
        window.__DEBUG_LOG('exit_fullscreen_start', {
          mode: mode,
          hasAnchor: !!anchorEl,
          containerStyles: containerEl.style.cssText
        });
      }
      
      // Clear the pseudo-fullscreen flag first
      delete containerEl.dataset.pseudoFullscreen;
      
      // Set fullscreen state to false immediately
      setIsFullscreen(false);
      
      // Hide the overlay during transition to prevent letterbox flash
      containerEl.style.opacity = '0';
      containerEl.style.pointerEvents = 'none';
      
      // Clear fullscreen styles but keep it positioned
      containerEl.style.position = 'fixed';
      containerEl.style.top = '0';
      containerEl.style.left = '0';
      containerEl.style.width = '100%';
      containerEl.style.height = '100%';
      containerEl.style.zIndex = '';
      containerEl.style.maxWidth = '';
      containerEl.style.maxHeight = '';
      containerEl.style.background = '';
      
      // Unlock orientation
      if (window.screen && window.screen.orientation && window.screen.orientation.unlock) {
        try {
          window.screen.orientation.unlock();
        } catch (err) {
          console.log('[GlobalVideoOverlay] Could not unlock orientation:', err);
        }
      }
      
      // Wait for viewport to settle before measuring
      const settleStart = Date.now();
      await waitForViewportSettle();
      const settleTime = Date.now() - settleStart;
      console.log('[GlobalVideoOverlay] Viewport settled, now measuring');
      
      if (window.__DEBUG_LOG) {
        window.__DEBUG_LOG('viewport_settle_complete', {
          settleTime: settleTime + 'ms'
        });
      }
      
      // Now measure and resize with stable viewport
      if (anchorEl && mode === 'inline') {
        const rect = anchorEl.getBoundingClientRect();
        
        if (window.__DEBUG_LOG) {
          window.__DEBUG_LOG('anchor_measurement', {
            measurements: rect,
            mode: mode,
            hasValidSize: rect.width > 0 && rect.height > 0
          });
        }
        
        if (rect.width && rect.height) {
          // Round dimensions to integers to avoid YouTube letterboxing
          const width = Math.round(rect.width);
          const height = Math.round(rect.height);
          
          setInlineRect({ ...rect });
          lastInlineRectRef.current = { ...rect };
          console.log('[GlobalVideoOverlay] Stable inline rect:', { width, height });
          
          // Resize the player with integer dimensions
          if (ytRef.current) {
            if (window.__DEBUG_LOG) {
              window.__DEBUG_LOG('player_resize', {
                playerSize: { width, height },
                message: 'Calling ytRef.setSize'
              });
            }
            ytRef.current.setSize(width, height);
            try {
              const iframe = ytRef.current.getIframe?.();
              if (iframe) {
                iframe.style.position = 'absolute';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.display = 'block';
                iframe.style.border = '0';
              }
            } catch {}
            
            // Force YouTube internal relayout to clear mobile letterboxing
            // This is needed because YouTube's internal layout cache doesn't always
            // recalculate after fullscreen exit on mobile devices
            if (window.__DEBUG_LOG) {
              window.__DEBUG_LOG('yt_relayout_start', {
                message: 'Starting YouTube internal relayout sequence'
              });
            }
            
            // Step 1: API double-resize nudge (±1px)
            ytRef.current.setSize(width, height + 1);
            if (window.__DEBUG_LOG) {
              window.__DEBUG_LOG('yt_nudge_setSize+1', {
                playerSize: { width, height: height + 1 }
              });
            }
            
            requestAnimationFrame(() => {
              if (!ytRef.current) return;
              
              // Restore to correct size
              ytRef.current.setSize(width, height);
              if (window.__DEBUG_LOG) {
                window.__DEBUG_LOG('yt_nudge_setSize_final', {
                  playerSize: { width, height }
                });
              }
              
              // Step 2: Iframe attribute manipulation with forced reflow
              try {
                const iframe = ytRef.current.getIframe?.();
                if (iframe) {
                  iframe.setAttribute('width', String(width));
                  iframe.setAttribute('height', String(height + 1));
                  void iframe.offsetHeight; // Force browser reflow
                  iframe.setAttribute('height', String(height));
                  
                  if (window.__DEBUG_LOG) {
                    window.__DEBUG_LOG('iframe_attr_nudge', {
                      message: 'Set iframe width/height attributes with reflow'
                    });
                  }
                }
              } catch (e) {
                console.log('[GlobalVideoOverlay] Iframe attribute nudge failed:', e);
              }
              
              // Step 3: Container nudge + window resize event
              if (containerEl) {
                const prevHeight = containerEl.style.height;
                containerEl.style.height = `${height + 1}px`;
                void containerEl.offsetHeight; // Force reflow
                containerEl.style.height = prevHeight || '';
                
                if (window.__DEBUG_LOG) {
                  window.__DEBUG_LOG('container_height_nudge', {
                    message: 'Container height nudged with reflow'
                  });
                }
              }
              
              // Dispatch window resize event to trigger YouTube's resize listener
              window.dispatchEvent(new Event('resize'));
              if (window.__DEBUG_LOG) {
                window.__DEBUG_LOG('window_resize_dispatch', {
                  message: 'Window resize event dispatched'
                });
              }
              
              // Step 4: Optional playback tick for iOS (last resort)
              // Only if still playing and on iOS devices
              const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
              if (isIOS) {
                requestAnimationFrame(() => {
                  if (!ytRef.current) return;
                  
                  try {
                    const state = ytRef.current.getPlayerState?.();
                    if (state === 1) { // Currently playing
                      const currentTime = ytRef.current.getCurrentTime?.();
                      if (typeof currentTime === 'number' && currentTime > 0) {
                        // Micro-seek to force internal refresh
                        ytRef.current.seekTo(currentTime + 0.001, true);
                        
                        if (window.__DEBUG_LOG) {
                          window.__DEBUG_LOG('playback_tick', {
                            message: 'Micro-seek performed for iOS',
                            time: currentTime
                          });
                        }
                      }
                    }
                  } catch (e) {
                    console.log('[GlobalVideoOverlay] Playback tick failed:', e);
                  }
                });
              }
              
              if (window.__DEBUG_LOG) {
                window.__DEBUG_LOG('yt_relayout_complete', {
                  message: 'YouTube internal relayout sequence completed'
                });
              }
            });
          }
          
          // Clear the temporary positioning and restore opacity
          containerEl.style.position = '';
          containerEl.style.top = '';
          containerEl.style.left = '';
          containerEl.style.width = '';
          containerEl.style.height = '';
        }
      }
      
      // Always ensure proper restoration when exiting fullscreen on mobile
      if (anchorEl) {
        console.log('[GlobalVideoOverlay] Ensuring proper position after fullscreen exit, mode:', mode);
        restore();
      }
      
      // Restore visibility
      containerEl.style.opacity = '';
      containerEl.style.pointerEvents = '';
      
      if (window.__DEBUG_LOG) {
        window.__DEBUG_LOG('exit_fullscreen_complete', {
          finalStyles: containerEl.style.cssText
        });
      }
      
      return;
    }
    
    // Standard fullscreen exit
    const exitFS = document.exitFullscreen || 
                   document.webkitExitFullscreen || 
                   document.webkitCancelFullScreen || // Note: capital S for older Safari
                   document.mozCancelFullScreen || 
                   document.msExitFullscreen;
    
    if (exitFS) {
      const promise = exitFS.call(document);
      
      if (promise && typeof promise.then === 'function') {
        promise
          .then(() => {
            console.log('[GlobalVideoOverlay] Successfully exited fullscreen');
            setIsFullscreen(false);
            // Reset container styles
            if (containerEl) {
              containerEl.style.width = '';
              containerEl.style.height = '';
              containerEl.style.maxWidth = '';
              containerEl.style.maxHeight = '';
            }
            // Ensure player resizes even if fullscreenchange doesn't fire
            setTimeout(() => {
              try {
                if (ytRef.current && containerEl) {
                  const r = containerEl.getBoundingClientRect();
                  ytRef.current.setSize(r.width, r.height);
                  // Re-apply iframe layout
                  try {
                    const iframe = ytRef.current.getIframe?.();
                    if (iframe) {
                      iframe.style.position = 'absolute';
                      iframe.style.top = '0';
                      iframe.style.left = '0';
                      iframe.style.width = '100%';
                      iframe.style.height = '100%';
                    }
                  } catch {}
                }
              } catch {}
            }, 100);
          })
          .catch((err) => {
            console.error('[GlobalVideoOverlay] Failed to exit fullscreen:', err);
            // Try pseudo-exit as fallback
            if (containerEl) {
              containerEl.style.width = '';
              containerEl.style.height = '';
              containerEl.style.maxWidth = '';
              containerEl.style.maxHeight = '';
              setIsFullscreen(false);
            }
          });
      } else {
        // For older browsers
        console.log('[GlobalVideoOverlay] Exited fullscreen (no promise)');
        setIsFullscreen(false);
        if (containerEl) {
          containerEl.style.width = '';
          containerEl.style.height = '';
          containerEl.style.maxWidth = '';
          containerEl.style.maxHeight = '';
        }
        // Ensure player resizes even if fullscreenchange doesn't fire
        setTimeout(() => {
          try {
            if (ytRef.current && containerEl) {
              const r = containerEl.getBoundingClientRect();
              ytRef.current.setSize(r.width, r.height);
              // Re-apply iframe layout
              try {
                const iframe = ytRef.current.getIframe?.();
                if (iframe) {
                  iframe.style.position = 'absolute';
                  iframe.style.top = '0';
                  iframe.style.left = '0';
                  iframe.style.width = '100%';
                  iframe.style.height = '100%';
                }
              } catch {}
            }
          } catch {}
        }, 100);
      }
    }
  }, [anchorEl, mode, restore, setInlineRect, waitForViewportSettle]);
  
  const toggleFullscreen = useCallback(async () => {
    console.log('[GlobalVideoOverlay] Toggle fullscreen, current state:', isFullscreen);
    if (isFullscreen) {
      // Capture current playback state before exiting (prefer our React state)
      let wasPlaying = !!isPlaying;
      let currentTimeSec = 0;
      try {
        const YT = window.YT;
        const st = ytRef.current?.getPlayerState?.();
        if (!wasPlaying) {
          // Fallback to YouTube state when our local state is uncertain
          wasPlaying = (st === YT?.PlayerState?.PLAYING) || (st === YT?.PlayerState?.BUFFERING);
        }
        currentTimeSec = ytRef.current?.getCurrentTime?.() || 0;
      } catch {}

      await exitFullscreen();

      // Mobile repair: mimic close->load to clear YT internal letterboxing state
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile && anchorEl && mode === 'inline' && videoId) {
        // Remember state to restore after remount
        resumeAfterRemountRef.current = { id: videoId, time: currentTimeSec, wasPlaying };
        setTimeout(() => {
          try { close(); } catch {}
          setTimeout(() => {
            try { load(videoId, { play: wasPlaying }); } catch {}
            // Try to restore time a bit after load
            setTimeout(() => {
              try {
                if (ytRef.current) {
                  const d = ytRef.current.getDuration?.() || 0;
                  const t = Math.max(0, Math.min(currentTimeSec, d - 0.05));
                  ytRef.current.seekTo(t, true);
                  if (wasPlaying) ytRef.current.playVideo?.();
                  else ytRef.current.pauseVideo?.();
                }
              } catch {}
            }, 500);
            setTimeout(() => {
              try {
                if (ytRef.current) {
                  const d = ytRef.current.getDuration?.() || 0;
                  const t = Math.max(0, Math.min(currentTimeSec, d - 0.05));
                  ytRef.current.seekTo(t, true);
                  if (wasPlaying) ytRef.current.playVideo?.();
                  else ytRef.current.pauseVideo?.();
                }
              } catch {}
            }, 1200);
          }, 120);
        }, 120);
      }
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, isPlaying, exitFullscreen, enterFullscreen, anchorEl, mode, close, load, videoId]);

  const handleMinimizeClick = useCallback(() => {
    if (isFullscreen) {
      // Ensure we exit fullscreen first to avoid stuck layout
      try { exitFullscreen(); } catch {}
      setTimeout(() => minimize('button'), 120);
    } else {
      minimize('button');
    }
  }, [isFullscreen, minimize, exitFullscreen]);

  const handleCloseClick = useCallback(() => {
    if (isFullscreen) {
      // Exit fullscreen first so UI returns to normal
      try { exitFullscreen(); } catch {}
      setTimeout(() => close(), 120);
    } else {
      close();
    }
  }, [isFullscreen, close, exitFullscreen]);

  // Navigate back to videos list on user-initiated minimize from watch page
  useEffect(() => {
    if (mode === 'mini' && minimizeSource === 'button') {
      if (/^\/videos\/[^/]+$/.test(location.pathname)) {
        navigate('/videos');
      }
    }
  }, [mode, minimizeSource, location.pathname, navigate]);

  // After navigation, when the anchor attaches, ensure we restore inline
  // Must be declared before any early returns to satisfy hooks rules
  useEffect(() => {
    if (wantInlineRestoreRef.current && mode === 'mini' && anchorEl) {
      restore();
      wantInlineRestoreRef.current = false;
    }
  }, [anchorEl, mode, restore]);

  // Keyboard shortcuts for video controls
  useEffect(() => {
    if (!isActive) return;
    
    const handleKeyPress = (e) => {
      // Ignore if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch(e.key.toLowerCase()) {
        case 'f':
          // F for fullscreen toggle
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'escape':
          // ESC exits fullscreen (including pseudo-fullscreen)
          if (isFullscreen) {
            e.preventDefault();
            toggleFullscreen();
          }
          break;
        case ' ':
          // Spacebar for play/pause
          e.preventDefault();
          handleTogglePlay();
          break;
        case 'arrowleft':
          // Left arrow to seek back 5 seconds
          if (ytRef.current) {
            e.preventDefault();
            const currentTime = ytRef.current.getCurrentTime();
            ytRef.current.seekTo(Math.max(0, currentTime - 5), true);
          }
          break;
        case 'arrowright':
          // Right arrow to seek forward 5 seconds
          if (ytRef.current) {
            e.preventDefault();
            const currentTime = ytRef.current.getCurrentTime();
            const duration = ytRef.current.getDuration();
            ytRef.current.seekTo(Math.min(duration, currentTime + 5), true);
          }
          break;
        default:
          // No action for other keys
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isActive, isFullscreen, toggleFullscreen, handleTogglePlay]);

  if (!isActive) return null;

  const styles = (() => {
    const base = {
      position: 'fixed',
      zIndex: 2147483000,
      // Never use display:none on the iframe container to avoid iOS issues
      visibility: 'visible',
      // Hide overlay visually on Shorts but keep in DOM (iOS safe)
      opacity: onShorts ? 0 : 1,
      pointerEvents: onShorts ? 'none' : 'auto',
    };
    
    // If in fullscreen, override all positioning
    if (isFullscreen) {
      return {
        ...base,
        top: 0,
        left: 0,
        width: '100vw',
        // Use dynamic viewport height with fallback
        height: CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh',
        background: '#000',
      };
    }

    if (mode === 'inline') {
      // Prefer current measured rect when non-zero
      const hasInlineSize = inlineRect && inlineRect.width && inlineRect.height;
      if (hasInlineSize) {
        return {
          ...base,
          top: `${inlineRect.top}px`,
          left: `${inlineRect.left}px`,
          width: `${inlineRect.width}px`,
          height: `${inlineRect.height}px`,
        };
      }

      // Try live anchor rect next
      const r = anchorEl?.getBoundingClientRect?.();
      if (r && r.width && r.height) {
        return {
          ...base,
          top: `${r.top}px`,
          left: `${r.left}px`,
          width: `${r.width}px`,
          height: `${r.height}px`,
        };
      }

      // Fall back to last known non-zero inline rect
      const last = lastInlineRectRef.current;
      if (last && last.width && last.height) {
        return {
          ...base,
          top: `${last.top}px`,
          left: `${last.left}px`,
          width: `${last.width}px`,
          height: `${last.height}px`,
        };
      }

      // As a final fallback, render with a mini-like size to avoid a 0x0 iframe
      return {
        ...base,
        bottom: '12px',
        right: '12px',
        width: 'clamp(180px, 50vw, 420px)',
        aspectRatio: '16 / 9',
        background: '#000',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      };
    }

    // mini mode with dynamic width clamp and 4-corner docking (mobile drag enabled)
    const margin = 12;
    const isLeft = /-left$/.test(dock);
    const isRight = /-right$/.test(dock);
    const isTop = /^top-/.test(dock);
    const isBottom = /^bottom-/.test(dock);
    const pos = isDragging && dragPos
      ? { top: `${dragPos.top}px`, left: `${dragPos.left}px` }
      : {
          ...(isTop ? { top: `${margin}px` } : { bottom: `${margin}px` }),
          ...(isRight ? { right: `${margin}px` } : { left: `${margin}px` }),
        };
    return {
      ...base,
      ...pos,
      width: 'clamp(180px, 50vw, 420px)',
      aspectRatio: '16 / 9',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
      borderRadius: '8px',
      overflow: 'hidden',
      background: '#000',
    };
  })();

  const chromeStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: mode === 'mini' ? 28 : 36,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '4px 6px',
    zIndex: 2,
    opacity: mode === 'inline' ? 1 : 0.95,
    pointerEvents: 'auto',
  };

  const btn = {
    border: 'none',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    borderRadius: 6,
    height: 32,
    minWidth: 32,
    padding: '0 8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  };

  const handlePointerDown = (e) => {
    if (mode !== 'mini') return;
    // Allow drag on touch, pen, and mouse (desktop testing too)
    const pointerType = e.pointerType || (e.touches ? 'touch' : 'mouse');
    if (!['touch', 'pen', 'mouse'].includes(pointerType)) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    const pt = e.touches?.[0] || e;
    const x = pt?.clientX;
    const y = pt?.clientY;
    const rect = containerRef.current?.getBoundingClientRect?.();
    if (typeof x === 'number' && typeof y === 'number' && rect) {
      dragRef.current = true;
      dragMovedRef.current = false;
      // Defer isDragging/dragPos until threshold exceeded
      dragStartRef.current = { x, y, top: rect.top, left: rect.left };
    }
  };
  const handlePointerMove = (e) => {
    if (mode !== 'mini') return;
    if (!dragRef.current || !dragStartRef.current) return;
    const pt = e.touches?.[0] || e;
    const x = pt?.clientX;
    const y = pt?.clientY;
    if (typeof x !== 'number' || typeof y !== 'number') return;
    const dx = x - dragStartRef.current.x;
    const dy = y - dragStartRef.current.y;
    if (!dragMovedRef.current) {
      if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) {
        try { e.preventDefault?.(); } catch {}
        return;
      }
      dragMovedRef.current = true;
      setIsDragging(true);
    }
    const el = containerRef.current;
    const elRect = el?.getBoundingClientRect?.();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const width = elRect?.width || 300;
    const height = elRect?.height || (vw * 9) / 16;
    const rawTop = dragStartRef.current.top + dy;
    const rawLeft = dragStartRef.current.left + dx;
    const top = Math.max(margin, Math.min(vh - height - margin, rawTop));
    const left = Math.max(margin, Math.min(vw - width - margin, rawLeft));
    setDragPos({ top, left });
    try { e.preventDefault?.(); } catch {}
  };
  const handlePointerUp = (e) => {
    if (mode !== 'mini') return;
    if (!dragRef.current) return;
    const didDrag = !!dragMovedRef.current;
    dragRef.current = false;
    setIsDragging(false);
    setDragPos(null);
    dragMovedRef.current = false;
    const pt = e.changedTouches?.[0] || e;
    const x = pt?.clientX;
    const y = pt?.clientY;
    if (didDrag && typeof x === 'number' && typeof y === 'number') {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const vert = y < vh / 2 ? 'top' : 'bottom';
      const side = x < vw / 2 ? 'left' : 'right';
      setDock(`${vert}-${side}`);
    }
  };

  const handleMaximizeClick = () => {
    try {
      if (!/^\/videos\/[^/]+$/.test(location.pathname) && videoId) {
        wantInlineRestoreRef.current = true; // defer inline restore until anchor attaches
        navigate(`/videos/${videoId}`);
      }
    } catch {}
    restore();
  };
  // inline restore effect moved above to satisfy hooks rules

  const content = (
    <div ref={containerRef} style={styles} aria-label="Global Video Overlay">
      <div style={chromeStyles}>
        {/* Drag handle (mini mode only) */}
        <div
          style={{
            color: 'rgba(255,255,255,0.85)',
            display: 'flex',
            alignItems: 'center',
            padding: mode === 'mini' ? '4px' : 0,
            cursor: mode === 'mini' ? 'grab' : 'default',
            touchAction: 'none',
            userSelect: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
          onTouchCancel={handlePointerUp}
        >
          {mode === 'mini' ? Icons.drag : null}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {mode === 'mini' ? (
            <>
              <button 
                style={btn} 
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onClick={handleMaximizeClick} 
                title="Maximize" 
                aria-label="Maximize"
              >
                {Icons.maximize}
              </button>
              <button 
                style={btn} 
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onClick={handleCloseClick} 
                title="Close" 
                aria-label="Close"
              >
                {Icons.close}
              </button>
            </>
          ) : (
            <>
              {!isFullscreen && (
                <button 
                  style={btn} 
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                  onClick={handleMinimizeClick} 
                  title="Minimize" 
                  aria-label="Minimize"
                >
                  {Icons.minimize}
                </button>
              )}
              <button 
                style={btn} 
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                onClick={handleCloseClick} 
                title="Close" 
                aria-label="Close"
              >
                {Icons.close}
              </button>
            </>
          )}
        </div>
      </div>
      <div
        ref={playerRef}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          // Keep visible for iOS; don't set display:none on any wrapper
          visibility: 'visible',
          pointerEvents: 'none', // prevent YT UI clicks; we handle our own UI
          background: '#000',
          zIndex: 1,
        }}
      />
      {/* Transparent drag surface to allow dragging from anywhere on video (mini mode), below chrome/controls */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        onTouchCancel={handlePointerUp}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1, // above iframe, below chrome/controls
          background: 'transparent',
          pointerEvents: mode === 'mini' ? 'auto' : 'none',
          touchAction: 'none',
          userSelect: 'none',
          cursor: mode === 'mini' ? 'grab' : 'default',
        }}
      />
      {/* Center play button overlay */}
      {!isPlaying && !onShorts && (
        <div
          onClick={handleTogglePlay}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 3,
            transition: 'background 0.2s, transform 0.2s',
            pointerEvents: 'auto',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)';
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
          }}
          title="Play"
          aria-label="Play"
        >
          {Icons.playLarge}
        </div>
      )}
      {/* Playback controls bar */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
          zIndex: 2,
          pointerEvents: 'auto',
        }}
      >
        <button 
          style={btn} 
          onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
          onClick={handleTogglePlay} 
          title={isPlaying ? 'Pause' : 'Play'} 
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? Icons.pause : Icons.play}
        </button>
        <div style={{ color: 'white', fontSize: 14, minWidth: 60, textAlign: 'center' }}>
          -{formatTime(Math.max(0, (duration || 0) - (currentTime || 0)))}
        </div>
        <div
          onClick={handleSeek}
          onMouseDown={handleSeek}
          onTouchStart={handleSeek}
          style={{
            position: 'relative',
            flex: 1,
            height: 8,
            borderRadius: 999,
            background: 'rgba(255,255,255,0.25)',
            cursor: 'pointer',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${duration ? (100 * currentTime) / duration : 0}%`,
              borderRadius: 999,
              background: 'white',
              transition: 'width 0.1s',
            }}
          />
        </div>
        <button 
          style={btn} 
          onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
          onClick={toggleFullscreen} 
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} 
          aria-label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? Icons.fullscreenExit : Icons.fullscreen}
        </button>
      </div>
    </div>
  );

  return createPortal(content, portalRoot);
}
